import React, { useRef } from 'react';
import { Printer, Download } from 'lucide-react';

// Garden images for each week (11 stages - week 12 shows certificate)
const GARDEN_WEEKS = [
  { week: 1, image: '/garden-week1.png', label: 'Dormant Garden' },
  { week: 2, image: '/garden-week2.png', label: 'First Signs of Life' },
  { week: 3, image: '/garden-week3.png', label: 'Sprouts Emerging' },
  { week: 4, image: '/garden-week4.png', label: 'Young Plants' },
  { week: 5, image: '/garden-week5.png', label: 'First Buds' },
  { week: 6, image: '/garden-week6.png', label: 'Flowers Opening' },
  { week: 7, image: '/garden-week7.png', label: 'Garden Blooming' },
  { week: 8, image: '/garden-week8.png', label: 'Summer Beauty' },
  { week: 9, image: '/garden-week9.png', label: 'Abundant Growth' },
  { week: 10, image: '/garden-week10.png', label: 'Peak Bloom' },
  { week: 11, image: '/garden-week11.png', label: 'Radiant Garden' },
];

function GardenVisualization({ garden, userName }) {
  const certificateRef = useRef(null);

  const handlePrintCertificate = () => {
    const formatDate = () => {
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Date().toLocaleDateString('en-US', options);
    };

    // Get the full URL for the garden image
    const gardenImageUrl = window.location.origin + '/garden-week12.png';

    const certificateHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Radiant 40 Certificate - ${userName || 'Graduate'}</title>
          <style>
            @page {
              size: landscape;
              margin: 0;
            }
            @media print {
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
            }
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            html, body {
              width: 100%;
              height: 100%;
              margin: 0;
              padding: 0;
            }
            body {
              font-family: Georgia, 'Times New Roman', serif;
            }
            .page-container {
              position: relative;
              width: 100%;
              height: 100vh;
              overflow: hidden;
            }
            .garden-background {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              object-fit: cover;
              filter: brightness(0.9) saturate(0.85);
            }
            .overlay {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              background: linear-gradient(135deg, rgba(254,247,240,0.25) 0%, rgba(253,242,248,0.2) 50%, rgba(254,252,232,0.25) 100%);
            }
            .certificate-wrapper {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 30px;
            }
            .certificate {
              background: linear-gradient(to right, rgba(255,251,235,0.95), rgba(255,255,255,0.97), rgba(255,251,235,0.95));
              border: 4px double #d4af37;
              border-radius: 16px;
              padding: 35px 50px;
              max-width: 750px;
              width: 100%;
              text-align: center;
              position: relative;
              box-shadow: 0 10px 40px rgba(0,0,0,0.2);
            }
            .corner { position: absolute; font-size: 20px; color: rgba(212, 175, 55, 0.6); }
            .corner-tl { top: 8px; left: 12px; }
            .corner-tr { top: 8px; right: 12px; transform: scaleX(-1); }
            .corner-bl { bottom: 8px; left: 12px; transform: scaleY(-1); }
            .corner-br { bottom: 8px; right: 12px; transform: scale(-1); }
            .flower-top {
              font-size: 40px;
              margin-bottom: 8px;
            }
            .title {
              color: #b45309;
              font-size: 12px;
              text-transform: uppercase;
              letter-spacing: 0.3em;
              font-weight: 600;
              margin-bottom: 15px;
            }
            .subtitle {
              color: #44403c;
              font-size: 12px;
              margin-bottom: 4px;
            }
            .name {
              color: #be185d;
              font-size: 42px;
              font-weight: bold;
              font-family: Georgia, serif;
              margin: 12px 0;
              text-shadow: 1px 1px 2px rgba(0,0,0,0.1);
            }
            .program-name {
              color: #1c1917;
              font-size: 24px;
              font-weight: bold;
              margin: 12px 0 4px 0;
            }
            .program-subtitle {
              color: #44403c;
              font-size: 12px;
              margin-bottom: 15px;
            }
            .divider {
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 10px;
              margin: 15px 0;
            }
            .divider-line {
              width: 60px;
              height: 1px;
              background: #d4af37;
            }
            .divider-flower {
              color: #f59e0b;
              font-size: 14px;
            }
            .quote {
              color: #44403c;
              font-style: italic;
              font-size: 14px;
              margin: 12px 0;
            }
            .date {
              color: #57534e;
              font-size: 11px;
              margin-top: 15px;
            }
          </style>
        </head>
        <body>
          <div class="page-container">
            <img src="${gardenImageUrl}" class="garden-background" alt="Garden Background" />
            <div class="overlay"></div>
            <div class="certificate-wrapper">
              <div class="certificate">
                <span class="corner corner-tl">❧</span>
                <span class="corner corner-tr">❧</span>
                <span class="corner corner-bl">❧</span>
                <span class="corner corner-br">❧</span>
                <div class="flower-top">🌺</div>
                <p class="title">Certificate of Completion</p>
                <p class="subtitle">This certifies that</p>
                <h1 class="name">${userName || 'You'}</h1>
                <p class="subtitle">has successfully completed the</p>
                <h2 class="program-name">Radiant 40</h2>
                <p class="program-subtitle">12-Week Transformation Journey</p>
                <div class="divider">
                  <div class="divider-line"></div>
                  <span class="divider-flower">✿</span>
                  <div class="divider-line"></div>
                </div>
                <p class="quote">"Your radiance shines from within and all around you."</p>
                <p class="date">${formatDate()}</p>
              </div>
            </div>
          </div>
        </body>
      </html>
    `;

    // Create a hidden iframe for printing
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    iframe.style.left = '-9999px';
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(certificateHTML);
    iframeDoc.close();

    // Wait for image to load then print
    iframe.onload = () => {
      // Give extra time for the background image to load
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        // Clean up after printing
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      }, 1000);
    };
  };

  if (!garden) {
    return (
      <div className="rounded-3xl overflow-hidden min-h-[400px] relative">
        <img
          src="/garden-week1.png"
          alt="Empty garden awaiting growth"
          className="w-full h-full object-cover"
          style={{ minHeight: '400px', maxHeight: '500px' }}
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
          <div className="text-center p-8 bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl max-w-md mx-4">
            <div className="text-4xl mb-4">🌱</div>
            <p className="text-stone-700 text-lg font-serif italic">
              Your garden awaits... Complete tasks to watch it bloom.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { garden_elements, progress_percent, total_days_completed } = garden;

  // Calculate which week the user has completed (each week = 7 days)
  const weeksCompleted = Math.min(12, Math.floor(total_days_completed / 7));
  
  // Get the current garden image based on weeks completed (max week 11 for garden images)
  const getCurrentWeekImage = () => {
    const weekIndex = Math.max(0, Math.min(10, weeksCompleted)); // Cap at index 10 (week 11)
    return GARDEN_WEEKS[weekIndex];
  };

  const currentWeek = getCurrentWeekImage();
  const isComplete = weeksCompleted >= 12;

  const getGardenDescription = () => {
    if (weeksCompleted < 1) return 'Your journey begins... plant the first seeds of change.';
    if (weeksCompleted < 3) return 'New life is emerging in your garden.';
    if (weeksCompleted < 5) return 'Your garden is starting to grow.';
    if (weeksCompleted < 7) return 'Beautiful blooms are appearing.';
    if (weeksCompleted < 9) return 'Your garden flourishes with vibrant life.';
    if (weeksCompleted < 11) return 'A magnificent sanctuary is taking shape.';
    return 'Your radiant garden is in magnificent full bloom!';
  };

  // Format current date for certificate
  const formatDate = () => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date().toLocaleDateString('en-US', options);
  };

  return (
    <div className="space-y-6" data-testid="garden-visualization">
      {/* Progress Bar */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-stone-700">Garden Growth</span>
          <span className="text-sm font-bold text-pink-600">Week {weeksCompleted} of 12</span>
        </div>
        <div className="w-full h-3 bg-stone-200 rounded-full overflow-hidden shadow-inner">
          <div
            className="h-full transition-all duration-1000 ease-out"
            style={{ 
              width: `${progress_percent}%`,
              background: 'linear-gradient(90deg, #84cc16, #22c55e, #10b981, #059669)'
            }}
          />
        </div>
      </div>

      {/* Garden Caption */}
      <p className="text-center text-lg text-stone-700 italic font-serif">
        "Complete weekly tasks and watch your garden bloom."
      </p>

      {/* Main Display - Garden Image OR Certificate */}
      {isComplete ? (
        /* Certificate Display for Week 12 Completion - Landscape with muted garden background */
        <div className="relative rounded-3xl overflow-hidden shadow-2xl">
          {/* Muted garden image background */}
          <div className="relative w-full h-[400px] md:h-[450px]">
            <img
              src="/garden-week12.png"
              alt="Garden background"
              className="w-full h-full object-cover"
              style={{
                filter: 'brightness(0.85) saturate(0.8) blur(0.5px)',
                opacity: 0.9
              }}
            />
            {/* Overlay for better certificate visibility */}
            <div 
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(135deg, rgba(254,247,240,0.3) 0%, rgba(253,242,248,0.25) 50%, rgba(254,252,232,0.3) 100%)'
              }}
            />
            
            {/* Certificate - Landscape orientation */}
            <div className="absolute inset-0 flex items-center justify-center p-4">
              <div 
                className="relative bg-gradient-to-r from-amber-50 via-white to-amber-50 rounded-2xl shadow-2xl px-12 py-8 w-full max-w-3xl border-4 border-double border-amber-400"
                style={{
                  backgroundImage: `
                    radial-gradient(circle at 5% 50%, rgba(212, 175, 55, 0.15) 0%, transparent 40%),
                    radial-gradient(circle at 95% 50%, rgba(212, 175, 55, 0.15) 0%, transparent 40%)
                  `
                }}
                data-testid="garden-main-image"
              >
                {/* Decorative top flower */}
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="text-4xl">🌺</div>
                </div>
                
                {/* Certificate content - Landscape layout */}
                <div className="flex items-center justify-between gap-8">
                  {/* Left decorative element */}
                  <div className="hidden md:flex flex-col items-center text-amber-400/60">
                    <div className="text-3xl">❧</div>
                    <div className="w-px h-16 bg-gradient-to-b from-amber-400/60 to-transparent"></div>
                  </div>
                  
                  {/* Main content */}
                  <div className="text-center flex-1 space-y-3">
                    <p className="text-amber-700 text-xs md:text-sm uppercase tracking-[0.3em] font-semibold">
                      Certificate of Completion
                    </p>
                    
                    <div>
                      <p className="text-stone-800 text-xs md:text-sm font-medium">This certifies that</p>
                      <h2 
                        className="text-3xl md:text-5xl font-serif font-bold text-pink-700 py-2"
                        style={{ 
                          fontFamily: 'Georgia, serif',
                          textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
                        }}
                      >
                        {userName || 'You'}
                      </h2>
                      <p className="text-stone-800 text-xs md:text-sm font-medium">has successfully completed the</p>
                    </div>
                    
                    <div>
                      <h3 className="text-xl md:text-2xl font-serif font-bold text-stone-900">
                        Radiant 40
                      </h3>
                      <p className="text-stone-800 text-xs md:text-sm font-medium">
                        12-Week Transformation Journey
                      </p>
                    </div>
                    
                    {/* Decorative line */}
                    <div className="flex items-center justify-center gap-3">
                      <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-400"></div>
                      <span className="text-amber-500">✿</span>
                      <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-400"></div>
                    </div>
                    
                    <p className="text-stone-800 italic font-serif text-xs md:text-sm font-medium">
                      "Your radiance shines from within and all around you."
                    </p>
                    
                    <p className="text-stone-700 text-xs font-medium">
                      {formatDate()}
                    </p>
                  </div>
                  
                  {/* Right decorative element */}
                  <div className="hidden md:flex flex-col items-center text-amber-400/60">
                    <div className="text-3xl transform scale-x-[-1]">❧</div>
                    <div className="w-px h-16 bg-gradient-to-b from-amber-400/60 to-transparent"></div>
                  </div>
                </div>
                
                {/* Corner decorations */}
                <div className="absolute top-2 left-2 text-amber-400/40 text-xl">❧</div>
                <div className="absolute top-2 right-2 text-amber-400/40 text-xl transform scale-x-[-1]">❧</div>
                <div className="absolute bottom-2 left-2 text-amber-400/40 text-xl transform scale-y-[-1]">❧</div>
                <div className="absolute bottom-2 right-2 text-amber-400/40 text-xl transform scale-[-1]">❧</div>
              </div>
            </div>
          </div>
          
          {/* Print Button */}
          <div className="absolute bottom-4 right-4 flex gap-2">
            <button
              onClick={handlePrintCertificate}
              className="flex items-center gap-2 bg-white/95 hover:bg-white text-stone-700 hover:text-pink-600 px-4 py-2 rounded-full shadow-lg transition-all duration-200 hover:scale-105 font-medium text-sm border border-stone-200"
              data-testid="print-certificate-btn"
            >
              <Printer className="w-4 h-4" />
              Print Certificate
            </button>
          </div>
        </div>
      ) : (
        /* Garden Image for Weeks 1-11 */
        <div className="relative rounded-3xl overflow-hidden shadow-2xl">
          <img
            src={currentWeek.image}
            alt={`Your Radiance Garden - ${currentWeek.label}`}
            className="w-full h-[400px] md:h-[500px] object-cover transition-all duration-1000"
            data-testid="garden-main-image"
          />
        
        {/* Animated Butterflies - appear after week 5 */}
        {weeksCompleted >= 5 && !isComplete && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {/* Butterfly 1 - Pink */}
            <div 
              className="butterfly absolute"
              style={{ 
                top: '25%', 
                left: '15%',
                animation: 'floatButterfly1 8s ease-in-out infinite',
              }}
            >
              <svg width="32" height="24" viewBox="0 0 32 24">
                <g className="butterfly-wings" style={{ animation: 'flapWings 0.3s ease-in-out infinite' }}>
                  <ellipse cx="8" cy="10" rx="7" ry="9" fill="#F9A8D4" opacity="0.85"/>
                  <ellipse cx="24" cy="10" rx="7" ry="9" fill="#F9A8D4" opacity="0.85"/>
                  <ellipse cx="8" cy="16" rx="5" ry="6" fill="#FBCFE8" opacity="0.85"/>
                  <ellipse cx="24" cy="16" rx="5" ry="6" fill="#FBCFE8" opacity="0.85"/>
                </g>
                <ellipse cx="16" cy="12" rx="1.5" ry="8" fill="#374151"/>
                <circle cx="16" cy="5" r="2" fill="#374151"/>
                <path d="M14 3 Q12 0 10 2" stroke="#374151" strokeWidth="1" fill="none"/>
                <path d="M18 3 Q20 0 22 2" stroke="#374151" strokeWidth="1" fill="none"/>
              </svg>
            </div>
            
            {/* Butterfly 2 - Yellow */}
            <div 
              className="butterfly absolute"
              style={{ 
                top: '45%', 
                right: '20%',
                animation: 'floatButterfly2 10s ease-in-out infinite',
                animationDelay: '2s'
              }}
            >
              <svg width="28" height="22" viewBox="0 0 32 24">
                <g className="butterfly-wings" style={{ animation: 'flapWings 0.25s ease-in-out infinite' }}>
                  <ellipse cx="8" cy="10" rx="7" ry="9" fill="#FDE047" opacity="0.85"/>
                  <ellipse cx="24" cy="10" rx="7" ry="9" fill="#FDE047" opacity="0.85"/>
                  <ellipse cx="8" cy="16" rx="5" ry="6" fill="#FEF08A" opacity="0.85"/>
                  <ellipse cx="24" cy="16" rx="5" ry="6" fill="#FEF08A" opacity="0.85"/>
                </g>
                <ellipse cx="16" cy="12" rx="1.5" ry="8" fill="#374151"/>
                <circle cx="16" cy="5" r="2" fill="#374151"/>
                <path d="M14 3 Q12 0 10 2" stroke="#374151" strokeWidth="1" fill="none"/>
                <path d="M18 3 Q20 0 22 2" stroke="#374151" strokeWidth="1" fill="none"/>
              </svg>
            </div>
            
            {/* Butterfly 3 - White/Light - appears after week 7 */}
            {weeksCompleted >= 7 && (
              <div 
                className="butterfly absolute"
                style={{ 
                  top: '60%', 
                  left: '60%',
                  animation: 'floatButterfly3 12s ease-in-out infinite',
                  animationDelay: '4s'
                }}
              >
                <svg width="24" height="18" viewBox="0 0 32 24">
                  <g className="butterfly-wings" style={{ animation: 'flapWings 0.35s ease-in-out infinite' }}>
                    <ellipse cx="8" cy="10" rx="7" ry="9" fill="#FFFFFF" opacity="0.9"/>
                    <ellipse cx="24" cy="10" rx="7" ry="9" fill="#FFFFFF" opacity="0.9"/>
                    <ellipse cx="8" cy="16" rx="5" ry="6" fill="#F3F4F6" opacity="0.9"/>
                    <ellipse cx="24" cy="16" rx="5" ry="6" fill="#F3F4F6" opacity="0.9"/>
                  </g>
                  <ellipse cx="16" cy="12" rx="1.5" ry="8" fill="#374151"/>
                  <circle cx="16" cy="5" r="2" fill="#374151"/>
                  <path d="M14 3 Q12 0 10 2" stroke="#374151" strokeWidth="1" fill="none"/>
                  <path d="M18 3 Q20 0 22 2" stroke="#374151" strokeWidth="1" fill="none"/>
                </svg>
              </div>
            )}
            
            {/* Butterfly 4 - Orange - appears after week 9 */}
            {weeksCompleted >= 9 && (
              <div 
                className="butterfly absolute"
                style={{ 
                  top: '35%', 
                  right: '35%',
                  animation: 'floatButterfly1 9s ease-in-out infinite',
                  animationDelay: '6s'
                }}
              >
                <svg width="26" height="20" viewBox="0 0 32 24">
                  <g className="butterfly-wings" style={{ animation: 'flapWings 0.28s ease-in-out infinite' }}>
                    <ellipse cx="8" cy="10" rx="7" ry="9" fill="#FB923C" opacity="0.85"/>
                    <ellipse cx="24" cy="10" rx="7" ry="9" fill="#FB923C" opacity="0.85"/>
                    <ellipse cx="8" cy="16" rx="5" ry="6" fill="#FDBA74" opacity="0.85"/>
                    <ellipse cx="24" cy="16" rx="5" ry="6" fill="#FDBA74" opacity="0.85"/>
                  </g>
                  <ellipse cx="16" cy="12" rx="1.5" ry="8" fill="#374151"/>
                  <circle cx="16" cy="5" r="2" fill="#374151"/>
                  <path d="M14 3 Q12 0 10 2" stroke="#374151" strokeWidth="1" fill="none"/>
                  <path d="M18 3 Q20 0 22 2" stroke="#374151" strokeWidth="1" fill="none"/>
                </svg>
              </div>
            )}
          </div>
        )}
        
        {/* Stage Label - only show when not complete */}
        {!isComplete && (
          <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg">
            <span className="text-stone-700 font-medium">{currentWeek.label}</span>
          </div>
        )}
      </div>
      )}

      {/* Week Progress Indicator */}
      <div className="flex justify-center gap-1.5">
        {GARDEN_WEEKS.map((week, idx) => (
          <div
            key={idx}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
              weeksCompleted >= week.week
                ? 'bg-green-500'
                : 'bg-stone-300'
            }`}
            title={`Week ${week.week}: ${week.label}`}
          />
        ))}
        {/* Week 12 indicator */}
        <div
          className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
            weeksCompleted >= 12
              ? 'bg-amber-500'
              : 'bg-stone-300'
          }`}
          title="Week 12: Certificate"
        />
      </div>

      {/* Butterfly Animation Styles */}
      <style>{`
        @keyframes floatButterfly1 {
          0%, 100% {
            transform: translate(0, 0) rotate(0deg);
          }
          25% {
            transform: translate(30px, -20px) rotate(5deg);
          }
          50% {
            transform: translate(50px, 10px) rotate(-3deg);
          }
          75% {
            transform: translate(20px, -15px) rotate(8deg);
          }
        }
        
        @keyframes floatButterfly2 {
          0%, 100% {
            transform: translate(0, 0) rotate(0deg);
          }
          25% {
            transform: translate(-25px, 15px) rotate(-5deg);
          }
          50% {
            transform: translate(-40px, -10px) rotate(3deg);
          }
          75% {
            transform: translate(-15px, 20px) rotate(-8deg);
          }
        }
        
        @keyframes floatButterfly3 {
          0%, 100% {
            transform: translate(0, 0) rotate(0deg);
          }
          33% {
            transform: translate(-20px, -25px) rotate(10deg);
          }
          66% {
            transform: translate(25px, -10px) rotate(-5deg);
          }
        }
        
        @keyframes flapWings {
          0%, 100% {
            transform: scaleX(1);
          }
          50% {
            transform: scaleX(0.3);
          }
        }
      `}</style>
    </div>
  );
}

export default GardenVisualization;
